import React, { useState } from "react";
import { motion } from "framer-motion";
import { X, ChevronLeft, ArrowRight, FileText, FileCheck, Download, CheckCircle2, Truck, Shield, ClipboardList, Loader2 } from "lucide-react";
import { ACCENT, GOLD, DARK, CARD, MID } from "./constants";
import { LiveDot } from "./MarketUI";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MARKET_TOKEN_KEY } from "./constants";

function ProfileDocRow({ label, ok, icon: Icon }) {
  return (
    <div className="flex items-center gap-3 py-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
      <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: ok ? "rgba(74,222,128,0.12)" : "rgba(255,255,255,0.05)" }}>
        <Icon className="w-3.5 h-3.5" style={{ color: ok ? "#4ADE80" : "#444" }} />
      </div>
      <div className="flex-1">
        <p className="font-head text-xs font-bold uppercase tracking-[0.15em] text-white/80">{label}</p>
      </div>
      <span
        className="font-head text-[9px] uppercase tracking-[0.12em] px-2 py-0.5"
        style={{ background: ok ? "rgba(74,222,128,0.12)" : "rgba(255,255,255,0.04)", color: ok ? "#4ADE80" : "#555", border: `1px solid ${ok ? "rgba(74,222,128,0.3)" : "#2a2a2a"}` }}
      >
        {ok ? "✓ Uploaded" : "○ Pending"}
      </span>
    </div>
  );
}

function StatPill({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex-1 min-w-0 px-3 py-2.5" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}>
      <p className="font-head text-[9px] uppercase tracking-[0.15em] text-white/30 mb-0.5">{label}</p>
      <p className="font-head text-sm font-bold text-white truncate">{value}</p>
    </div>
  );
}

export function CarrierDriverProfile({ driver, token, onClose, onBid, canBid }) {
  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const [dqLoading, setDqLoading] = useState(false);
  const [bundleLoading, setBundleLoading] = useState(false);
  const [dotRequesting, setDotRequesting] = useState(false);
  const [dotRequested, setDotRequested] = useState(driver.dot_history_requested || false);
  const photoUrl = driver.market_photo_path
    ? `${apiBase}/api/market/drivers/${driver.id}/photo`
    : null;
  const initials = `${driver.first_name?.[0] || ""}${driver.last_name?.[0] || ""}`.toUpperCase();

  const statusLabel = driver.market_status === "transferred" ? "Transferred"
    : driver.status === "approved" ? "Available"
    : driver.status === "rejected" ? "Closed" : "Active";
  const statusColor = driver.market_status === "transferred" ? GOLD
    : driver.status === "approved" ? "#4ADE80"
    : driver.status === "rejected" ? "#ef4444" : ACCENT;

  const endorsements = driver.endorsements ? driver.endorsements.split(/[,\s]+/).filter(Boolean) : [];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[200] flex"
      style={{ background: "rgba(0,0,0,0.92)", backdropFilter: "blur(12px)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <motion.div
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "spring", damping: 28, stiffness: 280 }}
        className="ml-auto h-full overflow-y-auto"
        style={{ width: "min(580px, 100vw)", background: DARK, borderLeft: `1px solid rgba(255,255,255,0.08)` }}
      >
        {/* Header bar */}
        <div className="sticky top-0 z-10 flex items-center gap-3 px-5 py-4" style={{ background: DARK, borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
          <button onClick={onClose} className="flex items-center gap-1.5 font-head text-[10px] uppercase tracking-[0.2em] text-white/40 hover:text-white transition-colors">
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
          <div className="flex-1" />
          <span className="font-head text-[10px] uppercase tracking-[0.15em] px-2 py-1" style={{ color: statusColor, border: `1px solid ${statusColor}40`, background: `${statusColor}12` }}>
            {statusLabel}
          </span>
        </div>

        {/* Profile hero */}
        <div className="px-6 pt-6 pb-4 flex items-start gap-5">
          <motion.div
            animate={{ boxShadow: ["0 0 0px rgba(255,77,0,0.3)", "0 0 28px rgba(255,77,0,0.55)", "0 0 0px rgba(255,77,0,0.3)"] }}
            transition={{ duration: 3, repeat: Infinity }}
            style={{ width: 88, height: 88, borderRadius: "50%", border: `2px solid rgba(255,77,0,0.4)`, overflow: "hidden", flexShrink: 0 }}
          >
            {photoUrl ? (
              <img src={photoUrl} alt={driver.first_name} style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center top" }} />
            ) : (
              <div className="w-full h-full flex items-center justify-center font-head font-black text-xl" style={{ background: MID, color: ACCENT }}>{initials}</div>
            )}
          </motion.div>

          <div className="flex-1 min-w-0">
            <p className="font-head font-black text-2xl uppercase tracking-[0.03em] text-white leading-tight">
              {driver.first_name} {driver.last_name}
            </p>
            {(driver.city || driver.state) && (
              <p className="text-sm text-white/40 mt-1 flex items-center gap-1.5">
                <Truck className="w-3 h-3" />
                {[driver.city, driver.state].filter(Boolean).join(", ")}
              </p>
            )}
            <div className="flex flex-wrap gap-1.5 mt-2">
              {driver.cdl_class && (
                <span className="font-head text-[10px] font-black uppercase px-2 py-0.5" style={{ background: ACCENT, color: "white" }}>{driver.cdl_class}</span>
              )}
              {driver.trailer_preference && (
                <span className="font-head text-[10px] uppercase tracking-[0.1em] px-2 py-0.5" style={{ background: "rgba(245,200,66,0.12)", color: GOLD, border: `1px solid rgba(245,200,66,0.25)` }}>{driver.trailer_preference}</span>
              )}
              {driver.bid_count > 0 && (
                <span className="font-head text-[10px] font-black uppercase px-2 py-0.5 flex items-center gap-1" style={{ background: GOLD, color: DARK }}>
                  <LiveDot color={DARK} size={5} /> {driver.bid_count} {driver.bid_count === 1 ? "BID" : "BIDS"}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Divider */}
        <div style={{ height: 2, background: `linear-gradient(90deg, ${ACCENT}, ${GOLD}40, transparent)`, margin: "0 24px" }} />

        {/* Quick stats */}
        <div className="px-6 py-4 flex gap-2 flex-wrap">
          <StatPill label="Experience" value={driver.cdl_experience} />
          <StatPill label="Trailer Pref." value={driver.trailer_preference} />
          <StatPill label="Drug Screen" value={driver.drug_screen_eligible_date ? `Eligible ${driver.drug_screen_eligible_date}` : null} />
          <StatPill label="Available" value={driver.start_eligible_date ? `From ${driver.start_eligible_date}` : "Immediately"} />
        </div>

        {/* Endorsements */}
        {endorsements.length > 0 && (
          <div className="px-6 pb-4">
            <p className="font-head text-[9px] uppercase tracking-[0.2em] text-white/30 mb-2">Endorsements</p>
            <div className="flex flex-wrap gap-1.5">
              {endorsements.map((e) => (
                <span key={e} className="font-head text-[10px] font-bold uppercase px-2 py-0.5" style={{ background: "rgba(255,77,0,0.1)", color: ACCENT, border: `1px solid rgba(255,77,0,0.25)` }}>{e}</span>
              ))}
            </div>
          </div>
        )}

        {/* Documents section */}
        <div className="px-6 pb-2">
          <p className="font-head text-[9px] uppercase tracking-[0.2em] text-white/30 mb-1">Documents on File</p>
          <div style={{ border: "1px solid rgba(255,255,255,0.07)" }}>
            {[
              { key: "cdl",            label: "CDL Front",           has: driver.has_cdl },
              { key: "cdl_back",       label: "CDL Back",            has: driver.has_cdl_back,        alwaysShow: false },
              { key: "previous_cdl_1", label: "Prev CDL 1",          has: driver.has_previous_cdl_1,  alwaysShow: false },
              { key: "previous_cdl_2", label: "Prev CDL 2",          has: driver.has_previous_cdl_2,  alwaysShow: false },
              { key: "medical",        label: "Medical Certificate",  has: driver.has_medical },
              { key: "mvr",            label: "MVR Report",           has: driver.has_mvr },
              { key: "psp",            label: "PSP Report",           has: driver.has_psp },
              { key: "clearinghouse",  label: "Clearinghouse",        has: driver.has_clearinghouse },
            ]
              .filter(d => d.alwaysShow !== false || d.has)
              .map(({ key, label, has }, idx, arr) => (
              <div key={key} style={{ borderBottom: idx < arr.length - 1 ? "1px solid rgba(255,255,255,0.05)" : "none" }}>
                {has && canBid ? (
                  <button
                    data-testid={`profile-doc-dl-${key}`}
                    onClick={() => {
                      fetch(`${apiBase}/api/market/carrier/drivers/${driver.id}/document/${key}`, {
                        headers: { Authorization: `Bearer ${token}` },
                      }).then(async r => {
                        if (!r.ok) {
                          // Show the actual server error (e.g. PII not cleared) instead of generic message
                          const errData = await r.json().catch(() => ({}));
                          import("sonner").then(({ toast }) => toast.error(errData.detail || "Download failed"));
                          return;
                        }
                        const blob = await r.blob();
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `${driver.first_name}-${driver.last_name}-${key}`;
                        // iOS Safari: must append to DOM and use setTimeout to stay in user-gesture
                        document.body.appendChild(a);
                        a.click();
                        setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 200);
                      }).catch(() => import("sonner").then(({ toast }) => toast.error("Download failed")));
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 transition-all group hover:brightness-110"
                    style={{ background: "rgba(34,197,94,0.04)" }}
                  >
                    <FileText className="w-4 h-4 shrink-0" style={{ color: "#22C55E" }} />
                    <span className="flex-1 text-left font-head text-xs text-white/80">{label}</span>
                    <span className="font-head text-[9px] uppercase tracking-[0.15em] px-2 py-0.5 flex items-center gap-1"
                      style={{ background: "rgba(34,197,94,0.1)", color: "#22C55E", border: "1px solid rgba(34,197,94,0.2)" }}>
                      <Download className="w-2.5 h-2.5" />Download
                    </span>
                  </button>
                ) : (
                  <div className="flex items-center gap-3 px-4 py-3" style={{ background: has ? "rgba(74,222,128,0.03)" : "transparent" }}>
                    <FileCheck className="w-4 h-4 shrink-0" style={{ color: has ? "#4ADE80" : "rgba(255,255,255,0.18)" }} />
                    <span className="flex-1 font-head text-xs" style={{ color: has ? "rgba(255,255,255,0.7)" : "rgba(255,255,255,0.25)" }}>{label}</span>
                    <span className="font-head text-[9px] uppercase tracking-[0.1em]" style={{ color: has ? "#4ADE80" : "rgba(255,255,255,0.2)" }}>
                      {has ? "On File" : "Pending"}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
          {canBid && (driver.has_cdl || driver.has_medical || driver.has_mvr || driver.has_psp || driver.has_clearinghouse) && (
            <p className="font-head text-[9px] text-white/20 mt-1.5 text-right uppercase tracking-[0.1em]">Click any document to download instantly</p>
          )}
        </div>

        {/* Background Checks */}
        <div className="px-6 py-4">
          <p className="font-head text-[9px] uppercase tracking-[0.2em] text-white/30 mb-2">Background &amp; Compliance</p>
          <div className="grid grid-cols-2 gap-2">
            {[
              { label: "Drug Screen",   ok: !!driver.drug_screen_eligible_date, desc: driver.drug_screen_eligible_date ? "Pre-employment completed" : "Pending" },
              { label: "Clearinghouse", ok: driver.has_clearinghouse,            desc: driver.has_clearinghouse ? "Enrolled" : "Pending" },
              { label: "MVR Check",     ok: driver.has_mvr,                      desc: driver.has_mvr ? "On file" : "Pending" },
              { label: "PSP Profile",   ok: driver.has_psp,                      desc: driver.has_psp ? "On file" : "Pending" },
            ].map(({ label, ok, desc }) => (
              <div key={label} className="p-3" style={{ background: ok ? "rgba(74,222,128,0.05)" : "rgba(255,255,255,0.02)", border: `1px solid ${ok ? "rgba(74,222,128,0.2)" : "rgba(255,255,255,0.06)"}` }}>
                <p className="font-head text-[9px] uppercase tracking-[0.12em] font-bold mb-0.5" style={{ color: ok ? "#4ADE80" : "#555" }}>{ok ? "✓" : "○"} {label}</p>
                <p className="text-[10px] text-white/40">{desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── DQ Packet & DOT History (carrier/admin only) ── */}
        {canBid && (
          <div className="px-6 pb-4">
            <p className="font-head text-[9px] uppercase tracking-[0.2em] text-white/30 mb-2">
              Driver Qualification Packet
            </p>
            <div className="space-y-2">
              {/* Download DQ Packet */}
              <button
                data-testid={`profile-dq-packet-btn-${driver.id}`}
                onClick={async () => {
                  const tok = token || localStorage.getItem(MARKET_TOKEN_KEY);
                  setDqLoading(true);
                  try {
                    const blob = await api.marketDownloadDQPacket(tok, driver.id);
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `hylas-dq-${driver.first_name}-${driver.last_name}.pdf`.toLowerCase().replace(/\s+/g, "-");
                    a.click();
                    URL.revokeObjectURL(url);
                    toast.success("DQ Packet downloaded");
                  } catch {
                    toast.error("Failed to generate DQ packet");
                  } finally {
                    setDqLoading(false);
                  }
                }}
                disabled={dqLoading}
                className="w-full flex items-center justify-between px-4 py-3 transition-all hover:brightness-110 disabled:opacity-60"
                style={{ background: "rgba(245,200,66,0.08)", border: "1px solid rgba(245,200,66,0.25)" }}
              >
                <div className="flex items-center gap-2.5">
                  {dqLoading ? <Loader2 className="w-4 h-4 animate-spin" style={{ color: GOLD }} /> : <Shield className="w-4 h-4" style={{ color: GOLD }} />}
                  <div className="text-left">
                    <p className="font-head text-xs font-bold uppercase tracking-[0.12em]" style={{ color: GOLD }}>Download DQ Packet</p>
                    <p className="text-[10px] text-white/35">FMCSA §391.21 compliant — drop into your DQ file</p>
                  </div>
                </div>
                <Download className="w-3.5 h-3.5 shrink-0" style={{ color: GOLD }} />
              </button>

              {/* Download DQ Bundle (all uploaded docs + cover) */}
              <button
                data-testid={`profile-dq-bundle-btn-${driver.id}`}
                onClick={async () => {
                  const tok = token || localStorage.getItem(MARKET_TOKEN_KEY);
                  setBundleLoading(true);
                  try {
                    const blob = await api.marketDownloadDQBundle(tok, driver.id);
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `hylas-dq-bundle-${driver.first_name}-${driver.last_name}.pdf`.toLowerCase().replace(/\s+/g, "-");
                    a.click();
                    URL.revokeObjectURL(url);
                    toast.success("DQ Bundle downloaded");
                  } catch {
                    toast.error("Failed to generate DQ bundle");
                  } finally {
                    setBundleLoading(false);
                  }
                }}
                disabled={bundleLoading}
                className="w-full flex items-center justify-between px-4 py-3 transition-all hover:brightness-110 disabled:opacity-60"
                style={{ background: "rgba(245,200,66,0.05)", border: "1px solid rgba(245,200,66,0.18)" }}
              >
                <div className="flex items-center gap-2.5">
                  {bundleLoading ? <Loader2 className="w-4 h-4 animate-spin" style={{ color: GOLD }} /> : <ClipboardList className="w-4 h-4" style={{ color: GOLD }} />}
                  <div className="text-left">
                    <p className="font-head text-xs font-bold uppercase tracking-[0.12em]" style={{ color: GOLD }}>Download DQ Bundle</p>
                    <p className="text-[10px] text-white/35">All uploaded docs in one PDF — CDL, MVR, PSP, Medical &amp; more</p>
                  </div>
                </div>
                <Download className="w-3.5 h-3.5 shrink-0" style={{ color: GOLD }} />
              </button>

              {/* Request 10-Year DOT History */}
              {dotRequested ? (
                <div
                  data-testid={`profile-dot-requested-${driver.id}`}
                  className="w-full flex items-center gap-2.5 px-4 py-3"
                  style={{ background: "rgba(34,197,94,0.06)", border: "1px solid rgba(34,197,94,0.2)" }}
                >
                  <CheckCircle2 className="w-4 h-4 shrink-0" style={{ color: "#22C55E" }} />
                  <div>
                    <p className="font-head text-xs font-bold uppercase tracking-[0.12em]" style={{ color: "#22C55E" }}>10-Year History Requested</p>
                    <p className="text-[10px] text-white/35">Driver has been notified in their hub</p>
                  </div>
                </div>
              ) : (
                <button
                  data-testid={`profile-request-dot-btn-${driver.id}`}
                  onClick={async () => {
                    const tok = token || localStorage.getItem(MARKET_TOKEN_KEY);
                    setDotRequesting(true);
                    try {
                      await api.marketRequestDotHistory(tok, driver.id);
                      setDotRequested(true);
                      toast.success("10-Year DOT history request sent to driver");
                    } catch {
                      toast.error("Failed to send request");
                    } finally {
                      setDotRequesting(false);
                    }
                  }}
                  disabled={dotRequesting}
                  className="w-full flex items-center justify-between px-4 py-3 transition-all hover:brightness-110 disabled:opacity-60"
                  style={{ background: "rgba(56,189,248,0.06)", border: "1px solid rgba(56,189,248,0.2)" }}
                >
                  <div className="flex items-center gap-2.5">
                    {dotRequesting ? <Loader2 className="w-4 h-4 animate-spin" style={{ color: "#38BDF8" }} /> : <ClipboardList className="w-4 h-4" style={{ color: "#38BDF8" }} />}
                    <div className="text-left">
                      <p className="font-head text-xs font-bold uppercase tracking-[0.12em]" style={{ color: "#38BDF8" }}>Request 10-Year DOT History</p>
                      <p className="text-[10px] text-white/35">Pings driver for 7-year supplemental addendum</p>
                    </div>
                  </div>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Bid CTA */}
        {canBid && (
          <div className="px-6 pb-8">
            <motion.button
              data-testid={`profile-bid-btn-${driver.id}`}
              onClick={() => { onClose(); onBid(driver); }}
              whileTap={{ scale: 0.97 }}
              className="w-full flex items-center justify-between px-5 py-4 font-head text-sm font-black uppercase tracking-[0.25em]"
              style={{ background: ACCENT, color: "white" }}
              onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 0 28px rgba(255,77,0,0.5)`; }}
              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; }}
            >
              Place Bid on {driver.first_name}
              <ArrowRight className="w-4 h-4" />
            </motion.button>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}
